import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { View, Switch, TextInput, Pressable, StyleSheet, Platform } from 'react-native';
import { YStack, Label, XStack, Separator } from 'tamagui';
import styles from '@stylesheet';
import { Foundation, FontAwesome, Entypo } from '@expo/vector-icons';
import updateStoreHandler from '../utils/updateStoreHandler';
import { Picker } from '@react-native-picker/picker';
import DataSetSelection from "@components/Global/DataSetSelection";
import RowComponent from '@components/Global/RowComponent/RowComponent';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { Select, MenuItem } from '@mui/material';
import toTitleCase from '@hooks/toTitleCase';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import * as Animatable from 'react-native-animatable';

const ComponentRenderer = ({ component, item, index }) => {
  const initialObject = item || {};
  const initialMap = useMemo(() => new Map(Object.entries(initialObject)), [initialObject]);
  const [validationOptions, setValidationOptions] = useState(initialMap);
  const { key, label, type, inputMode } = component;
  const [textValue, setTextValue] = useState(validationOptions.get(key));
  const [isFocused, setIsFocused] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const options = useMemo(() => validationOptions.get('enum') || [], [validationOptions]);
  const dataSetRef = React.useRef(null);
  const theme = themeSelector();

  // ALL HOOKS MUST BE AT THE TOP LEVEL - Move all hooks here
  const availableValues = useSelector((state: DefaultRootState) => state.uploadPreview.present.dataHeld);
  const [dependsVal, setDependsVal] = useState(() => ({
    field: validationOptions.get('dependsOn')?.field || '',
    value: validationOptions.get('dependsOn')?.value || ''
  }));

  const updateMap = (key, value) => {
    setValidationOptions((prevMap) => new Map(prevMap.set(key, value)));
  };

  const handleValidationChange = (value, key) => {
    updateMap(key, value);
    const options = { value, type: key };
    updateStoreHandler({ index, option: options });
  };

  const _handleAddOption = () => {
    if (textValue === '') return;
    const newOptions = [...options, textValue];
    handleValidationChange(newOptions, 'enum');
    setTextValue('');
  }

  const _handleRemoveOption = (optionToRemove) => {
    const newOptions = options.filter((option) => option !== optionToRemove);
    handleValidationChange(newOptions, 'enum');
  }

  const changeCallback = React.useCallback((value) => {
    handleValidationChange(value, key);
  }, [key, index]);

  const dataSetInitialValue = useMemo(() => {
    return validationOptions.get(key);
  }, [validationOptions, key]);

  // Define all callbacks at the top level
  const handleFieldChange = useCallback((event) => {
    const newField = event.target.value;
    console.log('Selected field:', newField);
    setDependsVal(prev => {
      const updatedVal = { ...prev, field: newField };
      handleValidationChange(updatedVal, key);
      return updatedVal;
    });
  }, [key]);

  const handleValueChangeText = useCallback((value) => {
    setDependsVal(prev => ({ ...prev, value }));
  }, []);

  const handleValueBlur = useCallback(() => {
    handleValidationChange(dependsVal, key);
  }, [dependsVal, key]);

  const canRenderDependsOnFields = availableValues.length > 0 && index > 0;

  // Now render based on type
  switch (type) {
    case 'dataSetSelection':
      return (
        <RowComponent
          title='Select Data Set'
          subtitle='Select a data set from the list of available datasets assigned under the user or company'
          customComponent={(
            <DataSetSelection ref={dataSetRef}
              changeCallback={changeCallback}
              initialValue={dataSetInitialValue as string | null}
            />
          )}
          />
      );
    case 'switch':
      return (
        <Pressable
          onPress={() => handleValidationChange(!validationOptions.get(key), key)}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            backgroundColor: theme ? 'white' : '#1e293b',
            borderWidth: 1,
            borderColor: theme ? '#e2e8f0' : '#374151',
            borderRadius: 12,
            paddingHorizontal: 16,
            paddingVertical: 14,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 3,
          }}
        >
          <Switch
            value={validationOptions.get(key)}
            onValueChange={(value) => handleValidationChange(value, key)}
            style={{ marginRight: 12 }}
          />
          <Label style={[localStyles.labelStyle, { flex: 1, color: theme ? '#1e293b' : '#f8fafc' }]}>
            {component.label}
          </Label>
        </Pressable>
      );
    case 'text':
    case 'textInput':
      return (
        <View style={{ flex: 1, marginVertical: 10 }}>
          <Label style={[localStyles.modernLabelStyle, { color: theme ? '#1e293b' : '#f8fafc' }]}>
            {component.label}
          </Label>
          <TextInput
            placeholder={label}
            placeholderTextColor={theme ? '#64748b' : '#94a3b8'}
            value={textValue || ''}
            onChangeText={(value) => setTextValue(value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              setIsFocused(false);
              handleValidationChange(textValue, key);
            }}
            inputMode={inputMode}
            style={{
              backgroundColor: theme ? 'white' : '#1e293b',
              borderWidth: 1,
              borderColor: isFocused ? '#3b82f6' : (theme ? '#e2e8f0' : '#374151'),
              borderRadius: 12,
              paddingHorizontal: 16,
              paddingVertical: 14,
              fontSize: 16,
              color: theme ? '#1e293b' : '#f8fafc',
              minHeight: 50,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 3,
              marginTop: 8,
            }}
          />
        </View>
      );
    case 'picker':
    case 'dropdown':
      const dropdownOptions = component.enum || options;
      const selectedValue = validationOptions.get(key);
      const selectedItem = dropdownOptions.find(opt => opt === selectedValue || (typeof opt === 'object' && opt.value === selectedValue));
      
      if (Platform.OS === 'web') {
        return (
          <View style={{ flex: 1, marginVertical: 10, zIndex: showDropdown ? 10000 : 1 }}>
            <Label style={[localStyles.modernLabelStyle, { color: theme ? '#1e293b' : '#f8fafc', marginBottom: 8 }]}>
              {component.label}
            </Label>
            <View style={{ position: 'relative', zIndex: showDropdown ? 10001 : 1 }}>
              <Pressable
                onPress={() => setShowDropdown(!showDropdown)}
                style={{
                  backgroundColor: theme ? 'white' : '#1e293b',
                  borderWidth: 1,
                  borderColor: showDropdown ? '#3b82f6' : (theme ? '#e2e8f0' : '#374151'),
                  borderRadius: 12,
                  paddingHorizontal: 16,
                  paddingVertical: 14,
                  minHeight: 50,
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.1,
                  shadowRadius: 4,
                  elevation: 3,
                }}
              >
                <Label style={{
                  color: selectedItem ? (theme ? '#1e293b' : '#f8fafc') : (theme ? '#64748b' : '#94a3b8'),
                  fontSize: 16,
                  fontWeight: selectedItem ? '600' : '400',
                  flex: 1
                }}>
                  {selectedItem ? (typeof selectedItem === 'object' ? selectedItem.label : selectedItem) : `Select ${component.label}...`}
                </Label>
                <FontAwesome
                  name={showDropdown ? 'chevron-up' : 'chevron-down'}
                  size={14}
                  color={theme ? '#64748b' : '#94a3b8'}
                />
              </Pressable>

              {showDropdown && (
                <View
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: theme ? 'white' : '#1e293b',
                    borderWidth: 1,
                    borderColor: theme ? '#e2e8f0' : '#374151',
                    borderRadius: 12,
                    marginTop: 4,
                    maxHeight: 200,
                    zIndex: 10002,
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 8 },
                    shadowOpacity: 0.15,
                    shadowRadius: 12,
                    elevation: 10002,
                  }}
                >
                  {dropdownOptions.map((option, idx) => {
                    const optionValue = typeof option === 'object' ? option.value : option;
                    const optionLabel = typeof option === 'object' ? option.label : option;
                    const isSelected = selectedValue === optionValue;
                    
                    return (
                      <Pressable
                        key={optionValue || idx}
                        onPress={() => {
                          handleValidationChange(optionValue, key);
                          setShowDropdown(false);
                        }}
                        style={{
                          paddingHorizontal: 16,
                          paddingVertical: 14,
                          backgroundColor: isSelected ? (theme ? '#e0f2fe' : '#1e40af20') : 'transparent',
                          borderBottomWidth: idx < dropdownOptions.length - 1 ? 1 : 0,
                          borderBottomColor: theme ? '#f1f5f9' : '#334155',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <Label style={{
                          color: isSelected ? (theme ? '#0369a1' : '#60a5fa') : (theme ? '#1e293b' : '#f8fafc'),
                          fontSize: 16,
                          fontWeight: isSelected ? '600' : '400',
                          flex: 1
                        }}>
                          {optionLabel}
                        </Label>
                        {isSelected && (
                          <FontAwesome
                            name="check"
                            size={14}
                            color={theme ? '#0369a1' : '#60a5fa'}
                          />
                        )}
                      </Pressable>
                    );
                  })}
                </View>
              )}

              {showDropdown && (
                <Pressable
                  onPress={() => setShowDropdown(false)}
                  style={{
                    position: 'absolute',
                    top: -1000,
                    left: -1000,
                    right: -1000,
                    bottom: -1000,
                    zIndex: 10000,
                  }}
                />
              )}
            </View>
          </View>
        );
      }
      
      return (
        <View style={{ flex: 1, marginVertical: 10 }}>
          <Label style={[localStyles.modernLabelStyle, { color: theme ? '#1e293b' : '#f8fafc', marginBottom: 8 }]}>
            {component.label}
          </Label>
          <View style={{
            backgroundColor: theme ? 'white' : '#1e293b',
            borderWidth: 1,
            borderColor: theme ? '#e2e8f0' : '#374151',
            borderRadius: 12,
            overflow: 'hidden',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 3,
          }}>
            <Picker
              selectedValue={validationOptions.get(key)}
              onValueChange={(itemValue, itemIndex) => handleValidationChange(itemValue, key)}
              style={{ color: theme ? '#1e293b' : '#f8fafc' }}
            >
              {dropdownOptions.map((option) => {
                const optionValue = typeof option === 'object' ? option.value : option;
                const optionLabel = typeof option === 'object' ? option.label : option;
                return <Picker.Item key={optionValue} label={optionLabel} value={optionValue} />;
              })}
            </Picker>
          </View>
        </View>
      );
    case 'dependsOn':
      return canRenderDependsOnFields ? (
        <View style={{ flex: 1, marginVertical: 10 }}>
          <Label style={[localStyles.modernLabelStyle, { color: theme ? '#1e293b' : '#f8fafc', marginBottom: 12 }]}>
            {component.label}
          </Label>
          <XStack gap={16} flex={1}>
            <YStack flex={1} gap={8}>
              <Label style={[localStyles.modernLabelStyle, { color: theme ? '#64748b' : '#94a3b8', fontSize: 14 }]}>
                Look at field
              </Label>
              <View style={{
                backgroundColor: theme ? 'white' : '#1e293b',
                borderWidth: 1,
                borderColor: theme ? '#e2e8f0' : '#374151',
                borderRadius: 12,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.1,
                shadowRadius: 4,
                elevation: 3,
              }}>
                <Select
                  value={dependsVal.field}
                  onChange={handleFieldChange}
                  fullWidth
                  style={{
                    backgroundColor: theme ? 'white' : '#1e293b',
                    color: theme ? '#1e293b' : '#f8fafc',
                    padding: 12,
                    borderRadius: 12,
                  }}
                  size="small"
                  MenuProps={{
                    PaperProps: {
                      style: {
                        backgroundColor: theme ? 'white' : '#1e293b',
                        borderColor: theme ? '#e2e8f0' : '#374151',
                        borderWidth: 1,
                        borderRadius: 12,
                        maxHeight: 200,
                      },
                    },
                  }}
                >
                  {availableValues
                    .filter((item, i) => i !== index)
                    .map((item, indexItem: number) => (
                      <MenuItem
                        key={item.value}
                        value={item.value}
                        style={{
                          backgroundColor: theme ? 'white' : '#1e293b',
                          color: theme ? '#1e293b' : '#f8fafc',
                        }}
                      >
                        {indexItem + ' '}{toTitleCase(item.title)}
                      </MenuItem>
                    ))}
                </Select>
              </View>
            </YStack>
            <YStack flex={1} gap={8}>
              <Label style={[localStyles.modernLabelStyle, { color: theme ? '#64748b' : '#94a3b8', fontSize: 14 }]}>
                If value is/contains:
              </Label>
              <TextInput
                placeholder="Value to match"
                placeholderTextColor={theme ? '#64748b' : '#94a3b8'}
                style={{
                  backgroundColor: theme ? 'white' : '#1e293b',
                  borderWidth: 1,
                  borderColor: isFocused ? '#3b82f6' : (theme ? '#e2e8f0' : '#374151'),
                  borderRadius: 12,
                  paddingHorizontal: 16,
                  paddingVertical: 14,
                  fontSize: 16,
                  color: theme ? '#1e293b' : '#f8fafc',
                  minHeight: 50,
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.1,
                  shadowRadius: 4,
                  elevation: 3,
                }}
                value={dependsVal.value}
                onChangeText={handleValueChangeText}
                onFocus={() => setIsFocused(true)}
                onBlur={() => {
                  setIsFocused(false);
                  handleValueBlur();
                }}
                inputMode={inputMode}
              />
            </YStack>
          </XStack>
        </View>
      ) : (
        <View style={{ flex: 1, marginVertical: 10 }}>
          <Label style={[localStyles.modernLabelStyle, { color: theme ? '#1e293b' : '#f8fafc', marginBottom: 8 }]}>
            {component.label}
          </Label>
          <View style={{
            backgroundColor: theme ? '#f8fafc' : '#0f172a',
            borderWidth: 1,
            borderColor: theme ? '#e2e8f0' : '#374151',
            borderRadius: 12,
            padding: 16,
          }}>
            <Label style={{
              color: theme ? '#64748b' : '#94a3b8',
              fontSize: 14,
              fontStyle: 'italic',
            }}>
              No fields available to depend on
            </Label>
          </View>
        </View>
      );
    case 'options':
      return (
        <View style={{ flex: 1, marginVertical: 10 }}>
          <Label style={[localStyles.modernLabelStyle, { color: theme ? '#1e293b' : '#f8fafc', marginBottom: 8 }]}>
            {component?.label} ({options?.length || 0})
          </Label>
          
          {/* Options List */}
          {options && options.length > 0 && (
            <View style={{
              backgroundColor: theme ? 'white' : '#1e293b',
              borderWidth: 1,
              borderColor: theme ? '#e2e8f0' : '#374151',
              borderRadius: 12,
              marginBottom: 12,
              overflow: 'hidden',
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 3,
            }}>
              {options.map((option, idx) => (
                <View
                  key={option || idx}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    paddingHorizontal: 16,
                    paddingVertical: 12,
                    borderBottomWidth: idx < options.length - 1 ? 1 : 0,
                    borderBottomColor: theme ? '#f1f5f9' : '#334155',
                    backgroundColor: idx % 2 ? (theme ? '#f8fafc' : '#0f172a') : 'transparent',
                  }}
                >
                  <Label style={{
                    flex: 1,
                    fontSize: 16,
                    fontWeight: '500',
                    color: theme ? '#1e293b' : '#f8fafc',
                  }}>
                    {option}
                  </Label>
                  <Pressable
                    onPress={() => _handleRemoveOption(option)}
                    style={{
                      padding: 8,
                      borderRadius: 8,
                      backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    }}
                  >
                    <Entypo name="cross" size={18} color="#EF4444" />
                  </Pressable>
                </View>
              ))}
            </View>
          )}

          {/* Add Option Input */}
          <View style={{
            flexDirection: 'row',
            gap: 8,
            alignItems: 'center',
          }}>
            <TextInput
              placeholder="Add an option"
              placeholderTextColor={theme ? '#64748b' : '#94a3b8'}
              value={textValue || ''}
              onChangeText={(value) => setTextValue(value)}
              onSubmitEditing={() => {
                if (textValue) {
                  _handleAddOption();
                }
              }}
              inputMode={inputMode}
              style={{
                flex: 1,
                backgroundColor: theme ? 'white' : '#1e293b',
                borderWidth: 1,
                borderColor: isFocused ? '#3b82f6' : (theme ? '#e2e8f0' : '#374151'),
                borderRadius: 12,
                paddingHorizontal: 16,
                paddingVertical: 14,
                fontSize: 16,
                color: theme ? '#1e293b' : '#f8fafc',
                minHeight: 50,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.1,
                shadowRadius: 4,
                elevation: 3,
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            />
            <Pressable
              onPress={() => _handleAddOption()}
              disabled={!textValue || textValue.trim() === ''}
              style={{
                backgroundColor: textValue && textValue.trim() ? '#10B981' : (theme ? '#e2e8f0' : '#374151'),
                borderRadius: 12,
                padding: 14,
                minWidth: 50,
                minHeight: 50,
                justifyContent: 'center',
                alignItems: 'center',
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.1,
                shadowRadius: 4,
                elevation: 3,
                opacity: textValue && textValue.trim() ? 1 : 0.5,
              }}
            >
              <Entypo name="plus" size={20} color="white" />
            </Pressable>
          </View>
        </View>
      );
    default:
      return null;
  }
}

export default React.memo(ComponentRenderer);

const localStyles = StyleSheet.create({
  labelStyle: {
    fontWeight: 'bold',
    fontSize: 16,
    marginBottom: 5,
  },
  modernLabelStyle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  }
})
